import { Button, Typography, Space } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { RecordGroup } from '#types/System.types';
import dayjs from 'dayjs';

const { Text } = Typography;

interface RecordGroupColumnsParams {
    handleEditGroup: (groupId: string, group: RecordGroup) => void;
    handleDeleteGroup: (groupId: string) => void;
}

interface RecordGroupTableData extends RecordGroup {
    groupId: string;
    key: string;
}

/**
 * Creates column definitions for the record groups table.
 * Defines columns for label, description, last modified date, and actions.
 */
export const recordGroupColumns = ({
    handleEditGroup,
    handleDeleteGroup,
}: RecordGroupColumnsParams): ColumnsType<RecordGroupTableData> => [
    {
        title: (
            <Space vertical size={0}>
                <Text>Name</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>Group ID</Text>
            </Space>
        ),
        key: 'shortLabel',
        width: 250,
        defaultSortOrder: 'ascend',
        sorter: (a, b) => {
            const aValue = a.description?.shortLabel || '';
            const bValue = b.description?.shortLabel || '';
            return aValue.localeCompare(bValue);
        },
        render: (_, record) => {
            const value = record.description?.shortLabel || 'Unnamed Group';
            return (
                <Space vertical size={0}>
                    <Text strong>{value}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {record.groupId}
                    </Text>
                </Space>
            );
        },
    },
    {
        title: 'Description',
        key: 'longDescription',
        ellipsis: true,
        sorter: (a, b) => {
            const aValue = a.description?.longDescription || '';
            const bValue = b.description?.longDescription || '';
            return aValue.localeCompare(bValue);
        },
        render: (_, record) => {
            const value = record.description?.longDescription || '';
            return <Text>{value || '-'}</Text>;
        },
    },
    {
        title: 'Last Modified',
        key: 'lastModified',
        width: 140,
        sorter: (a, b) => {
            const getTimestamp = (record: RecordGroupTableData) => {
                const lastModified = record.meta?.lastModified;
                if (!lastModified) return 0;

                // Handle Firebase Timestamp
                if (typeof lastModified === 'object' && 'toDate' in lastModified) {
                    return lastModified.toDate().getTime();
                }
                // Handle Date object
                if (lastModified instanceof Date) {
                    return lastModified.getTime();
                }
                // For FieldValue or other types, return 0
                return 0;
            };

            return getTimestamp(a) - getTimestamp(b);
        },
        render: (_, record) => {
            const lastModified = record.meta?.lastModified;
            if (!lastModified) return <Text type="secondary">-</Text>;

            let dayjsDate;
            // Handle Firebase Timestamp
            if (typeof lastModified === 'object' && 'toDate' in lastModified) {
                dayjsDate = dayjs(lastModified.toDate());
            } else if (lastModified instanceof Date) {
                // Handle Date object
                dayjsDate = dayjs(lastModified);
            } else {
                // For FieldValue or other types, show current date
                dayjsDate = dayjs();
            }

            // Validate the date
            if (!dayjsDate.isValid()) {
                return <Text type="secondary">Invalid date</Text>;
            }

            return (
                <Space vertical size={0}>
                    <Text>{dayjsDate.format('MM/DD/YYYY')}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {dayjsDate.format('h:mm A')}
                    </Text>
                </Space>
            );
        },
    },
    {
        title: '',
        key: 'actions',
        width: 80,
        fixed: 'right',
        render: (_, record) => {
            return (
                <Space size="small">
                    <Button
                        type='text'
                        icon={<EditOutlined />}
                        onClick={() => handleEditGroup(record.groupId, record)}
                        aria-label={`Edit ${record.description?.shortLabel || 'group'}`}
                    />
                    <Button
                        type='text'
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteGroup(record.groupId)}
                        aria-label={`Delete ${record.description?.shortLabel || 'group'}`}
                    />
                </Space>
            );
        },
    },
];